import { defineComponent, h } from 'vue'

/**
 * 产品能力 / 安全区的双色图标（v6 设计稿）：48 网格，线宽 2.5，线条跟随 currentColor，
 * 每个图标一处品牌橙实心（official-duo-accent）；压在实心上的对勾、加号是 official-duo-knock，
 * 浅色底白色、深色区墨色，都在样式里按区块定。
 *
 * 和 Icon.ts 一样用 vnode 逐个生成，不走 svg.innerHTML（老 WebView 上不可靠）。
 */
type Part = [tag: string, attrs: Record<string, string>, kind?: 'line' | 'accent' | 'knock']

const duoIcons: Record<string, Part[]> = {
  // 蜂巢外壳里一个独立的应用
  space: [
    ['path', { d: 'M24 5L40.5 14.5V33.5L24 43L7.5 33.5V14.5Z' }, 'line'],
    ['rect', { x: '16', y: '16', width: '16', height: '16', rx: '4.5' }, 'accent'],
  ],
  // 盾牌，校验通过
  shield: [
    ['path', { d: 'M24 5L39 10.5V22.5C39 32 32.5 39.5 24 43C15.5 39.5 9 32 9 22.5V10.5Z' }, 'line'],
    ['circle', { cx: '24', cy: '23', r: '9' }, 'accent'],
    ['path', { d: 'M19.8 23.2L22.9 26.2L28.4 20.4' }, 'knock'],
  ],
  // 输入框里的邀请码，光标到位
  code: [
    ['rect', { x: '5', y: '13', width: '38', height: '22', rx: '7' }, 'line'],
    ['circle', { cx: '14', cy: '24', r: '2.6', class: 'official-duo-dot' }],
    ['circle', { cx: '22.5', cy: '24', r: '2.6', class: 'official-duo-dot' }],
    ['rect', { x: '30', y: '17.5', width: '5.5', height: '13', rx: '2.75' }, 'accent'],
  ],
  // 夹子是识别点，内容是一行码
  clipboard: [
    ['path', { d: 'M16 9H13A5 5 0 0 0 8 14V38A5 5 0 0 0 13 43H35A5 5 0 0 0 40 38V14A5 5 0 0 0 35 9H32' }, 'line'],
    ['rect', { x: '16', y: '4.5', width: '16', height: '9', rx: '3.5' }, 'accent'],
    ['path', { d: 'M16 24H32M16 32H25' }, 'line'],
  ],
  // 账号居中，两段同步弧
  sync: [
    ['path', { d: 'M41.5 21A18 18 0 0 0 9.5 13.5M6.5 27A18 18 0 0 0 38.5 34.5' }, 'line'],
    ['path', { d: 'M9 5.5V14H17.5M39 42.5V34H30.5' }, 'line'],
    ['circle', { cx: '24', cy: '24', r: '7' }, 'accent'],
  ],
  // 桌面图标格，第四格是新增
  desktop: [
    ['rect', { x: '6', y: '6', width: '15', height: '15', rx: '4.5' }, 'line'],
    ['rect', { x: '27', y: '6', width: '15', height: '15', rx: '4.5' }, 'line'],
    ['rect', { x: '6', y: '27', width: '15', height: '15', rx: '4.5' }, 'line'],
    ['rect', { x: '27', y: '27', width: '15', height: '15', rx: '4.5' }, 'accent'],
    ['path', { d: 'M34.5 31V38M31 34.5H38' }, 'knock'],
  ],
  // 安全区：传输加密
  lock: [
    ['rect', { x: '8', y: '20', width: '32', height: '23', rx: '6' }, 'line'],
    ['path', { d: 'M15 20V14A9 9 0 0 1 33 14V20' }, 'line'],
    ['circle', { cx: '24', cy: '31.5', r: '4.5' }, 'accent'],
  ],
  // 安全区：安装包校验
  package: [
    ['path', { d: 'M26 43H13A5 5 0 0 1 8 38V10A5 5 0 0 1 13 5H26L38 17V25' }, 'line'],
    ['path', { d: 'M26 5V17H38' }, 'line'],
    ['circle', { cx: '35', cy: '35', r: '9' }, 'accent'],
    ['path', { d: 'M31 35.2L34 38.1L39 32.6' }, 'knock'],
  ],
  // 安全区：凭据不出账号页
  credential: [
    ['rect', { x: '5', y: '10', width: '38', height: '28', rx: '6' }, 'line'],
    ['circle', { cx: '16.5', cy: '24', r: '5.5' }, 'accent'],
    ['path', { d: 'M28 20H36M28 28H36' }, 'line'],
  ],
  // 安全区：应用相互隔离
  isolate: [
    ['rect', { x: '5', y: '9', width: '16', height: '30', rx: '5' }, 'line'],
    ['rect', { x: '27', y: '9', width: '16', height: '30', rx: '5' }, 'accent'],
  ],
}

export const DuoIcon = defineComponent({
  name: 'DuoIcon',
  props: {
    name: { type: String, required: true },
  },
  setup(props) {
    return () =>
      h(
        'svg',
        {
          xmlns: 'http://www.w3.org/2000/svg',
          viewBox: '0 0 48 48',
          'aria-hidden': 'true',
          focusable: 'false',
          class: 'official-duo-icon',
        },
        (duoIcons[props.name] || []).map(([tag, attrs, kind]) =>
          h(tag, kind ? { ...attrs, class: `official-duo-${kind}` } : attrs),
        ),
      )
  },
})
